import { cookies, ILogService, IPromise, IQService, jwt } from 'angular';
import { StorageService } from '../../providers/storage/storage.service';
import { GmcApi } from '../../constants/gmc-api';
import { AuthData, IAuthData } from './auth-data';
import { CommonRegData } from '../../../states/auth/models/common-reg-data';
import { isObject } from 'lodash-es';
import { WebsocketService } from '../websocket/websocket.service';
import { ApiService } from '../api-service/api.service';
import { IUser, User } from '../../entities/user';
import { RoleService } from '../role/role.service';
import { UserService } from '../user/user.service';
import { UserRoles } from '../role/role-statuses';
import { IProjectConfig, PROJECT_CONFIG } from '../../constants/project-config.constant';
import { StateService } from '@uirouter/angularjs';

export interface ILoginData {
  username?: string;
  password?: string;
}

export interface ILoginResponse extends IUser {
  token: string;
}

interface IToken extends IAuthData {
  iat?: number;
}

export class AuthService {
  static $inject = [
    'StorageService',
    '$state',
    'GmcApi',
    'jwtHelper',
    '$log',
    '$q',
    '$cookies',
    'WebsocketService',
    'ApiService',
    'RoleService',
    'UserService',
    PROJECT_CONFIG
  ];
  authData: AuthData = new AuthData();
  user: User = null;
  userRoles: UserRoles;
  private isSession = false;

  constructor( private storageService: StorageService,
               private $state: StateService,
               private gmcApi: GmcApi,
               private jwtHelper: jwt.IJwtHelper,
               private $log: ILogService,
               private $q: IQService,
               private $cookies: cookies.ICookiesService,
               private websocketService: WebsocketService,
               private apiService: ApiService,
               private roleService: RoleService,
               private userService: UserService,
               private projectConfig: IProjectConfig ) {
    this.userRoles = new UserRoles();
    this.init();
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  isCandidate(): boolean {
    return this.user.baseRoleName === 'Candidate';
  }

  isEmployer(): boolean {
    return this.user.baseRoleName === 'Employer';
  }

  login( data: ILoginData, session: boolean ): IPromise<User> {
    return this.apiService.post<ILoginResponse>('/user/signin', data)
      .then(( authData ) => {
        this.authUser(authData, session);
        return this.user;
      });
  }

  logout(): IPromise<any> {
    return this.$q(( resolve ) => {
      this.clearAuth();
      this.websocketService.disconnect();
      resolve(true);
      this.storageService.remove('isOpenModalComponent');
    });
  }

  register( data: CommonRegData ): IPromise<any> {
    const DEFAULT_REG_ERROR = 'Registration Error';
    return this.apiService.post('/user/register', data)
      .then(( res ) => {
        return res;
      })
      .catch(( err ) => {
        const errData = err.data;
        let serverError = '';
        if ( isObject(errData.text) && isObject(errData.text.errors) ) {
          serverError = errData.text._message || errData.text.message || DEFAULT_REG_ERROR;
        } else if ( errData.text ) {
          serverError = errData.text;
        } else {
          serverError = DEFAULT_REG_ERROR;
        }
        throw (serverError);
      });
  }

  sendActivationLinkEmail( username: string ): IPromise<any> {
    return this.apiService.post('/user/sendActivationLinkEmail', { username: username, message: 'No username found'})
      .then(( res ) => {
        console.log(res);
        return res;
      });
  }

  activateAccount( code: string ): IPromise<any> {
    return this.apiService.post<ILoginResponse>('/user/activate', { activate_code: code })
      .then(( data ) => {
        this.authUser(data, true);
        return this.loadUser();
      });
  }

  verifyToken() {
    return this.apiService.post('/user/verifyToken').then((res) => {
      return res;
    });
  }

  getToken(): string {
    return this.storageService.get('token', this.isSession);
  }

  getAuthHeader(): string {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  }

  clearAuth() {
    this.storageService.remove('token', true);
    this.storageService.remove('token');
    this.user = null;
    this.userRoles = new UserRoles();
    // this.authData = new AuthData();
  }

  authUser( data: ILoginResponse, session: boolean ) {
    this.isSession = session;
    this.saveToken(data.token);
    if (data._id) {
      this.user = new User(data);
    } else {
      this.loadUser();
    }
    this.user.photo = this.user.photo || this.projectConfig.defaultPhoto;
    this.userRoles = this.roleService.getRolesStatuses(Number(data.role));
    this.resolveRoleNames();
    this.websocketService.connect(this.getToken());
    // this.authData = new AuthData(this.decodeToken());
  }

  loadUser(): IPromise<User> {
    if ( !this.isLoggedIn() ) {
      return this.$q.resolve()
        .then(() => {
          this.user = null;
          this.userRoles = new UserRoles();
          return this.user;
        });
    }
    return this.userService.getUserById()
      .then(( user ) => {
        this.user = user;
        this.userRoles = this.roleService.getRolesStatuses(user.role);
        this.resolveRoleNames();
        return this.user;
      });
  }

  private init() {
    this.checkSession();
    if ( this.isLoggedIn() ) {
      this.websocketService.connect(this.getToken());
    }
  }


  private saveToken( token: string ) {
    this.storageService.put('token', token, this.isSession);
  }

  /*private decodeToken(): IToken {
    let decodedToken: IToken = {};
    if ( this.isLoggedIn() ) {
      try {
        decodedToken = this.jwtHelper.decodeToken(this.storageService.get('token', this.isSession)) as IToken;
      } catch ( err ) {
        this.$log.error(err);
        this.clearAuth();
      }
    }
    return decodedToken;
  }*/

  private checkSession() {
    if ( this.storageService.get('token', true) ) {
      return this.isSession = true;
    }
  }

  private resolveRoleNames() {
    this.user.baseRoleName = this.roleService.getBaseRoleName(this.user.role);
    this.user.subRoleName = this.roleService.getSubRoleName(this.user.role);
  }
}



// WEBPACK FOOTER //
// ./app/core/services/auth/auth.service.ts